<template>
  <div class="pw-bar" ref="root" :style="{ height: `${height}px` }">
    <svg
      v-if="width > 0"
      :width="width"
      :height="height"
      role="img"
      :aria-label="ariaLabel"
      @mouseleave="hover = null"
    >
      <!-- Grid + y ticks -->
      <g v-for="tick in ticks" :key="`t${tick}`">
        <line
          :x1="pad.left"
          :x2="width - pad.right"
          :y1="y(tick)"
          :y2="y(tick)"
          :class="tick === 0 ? 'pw-bar__baseline' : 'pw-bar__grid'"
        />
        <text
          :x="pad.left - 8"
          :y="y(tick)"
          class="pw-bar__tick"
          text-anchor="end"
          dominant-baseline="middle"
        >
          {{ formatTick(tick) }}
        </text>
      </g>

      <!-- Hover wash -->
      <rect
        v-if="hover !== null"
        :x="bandX(hover)"
        :y="pad.top"
        :width="band"
        :height="plotHeight"
        class="pw-bar__wash"
      />

      <!-- Bars -->
      <g
        v-for="(label, i) in labels"
        :key="`b${i}`"
        :opacity="highlightIndex != null && highlightIndex !== i ? 0.4 : 1"
      >
        <path
          v-for="(s, j) in series"
          :key="`b${i}-${j}`"
          :d="barPath(i, j, s.values[i])"
          :fill="s.color"
        />
        <text
          v-if="stacked && showTotals && stackTotal(i) > 0"
          :x="bandX(i) + band / 2"
          :y="y(stackTotal(i)) - 6"
          class="pw-bar__total"
          text-anchor="middle"
        >
          {{ formatValue(stackTotal(i)) }}
        </text>
      </g>

      <!-- X labels -->
      <text
        v-for="(label, i) in labels"
        :key="`x${i}`"
        :x="bandX(i) + band / 2"
        :y="height - 6"
        class="pw-bar__tick"
        :class="{ 'pw-bar__tick--strong': highlightIndex === i }"
        text-anchor="middle"
      >
        {{ label }}
      </text>

      <!-- Hit targets, a full band each -->
      <rect
        v-for="(label, i) in labels"
        :key="`h${i}`"
        :x="bandX(i)"
        :y="0"
        :width="band"
        :height="height"
        fill="transparent"
        @mouseenter="hover = i"
      />
    </svg>

    <div
      v-if="hover !== null"
      class="pw-tooltip"
      :style="tooltipStyle"
    >
      <div class="pw-tooltip__title">{{ tooltipLabels[hover] || labels[hover] }}</div>
      <div v-for="s in series" :key="s.name" class="pw-tooltip__row">
        <span class="pw-tooltip__key" :style="{ background: s.color }"></span>
        <span class="pw-tooltip__name">{{ s.name }}</span>
        <span class="pw-tooltip__value">{{ formatValue(s.values[hover]) }}</span>
      </div>
      <div v-if="stacked" class="pw-tooltip__row pw-tooltip__row--total">
        <span class="pw-tooltip__name">Total</span>
        <span class="pw-tooltip__value">{{ formatValue(stackTotal(hover)) }}</span>
      </div>
    </div>

    <div v-if="isEmpty" class="pw-bar__empty">{{ emptyText }}</div>
  </div>
</template>

<script>
// Vertical bar chart, one or more series grouped (or stacked) per category,
// on a single y-axis. Plain SVG; width follows the container.
export default {
  name: "BarChart",
  props: {
    labels: { type: Array, required: true },
    // Full label per category for the tooltip, e.g. "Sep 2026"
    tooltipLabels: { type: Array, default: () => [] },
    // [{ name, color, values: [] }]
    series: { type: Array, required: true },
    height: { type: Number, default: 200 },
    formatTick: { type: Function, default: (v) => String(v) },
    formatValue: { type: Function, default: (v) => String(v) },
    // Category to emphasise; the others are dimmed
    highlightIndex: { type: Number, default: null },
    emptyText: { type: String, default: "No data for this period" },
    ariaLabel: { type: String, default: "Bar chart" },
    // One bar per category, series piled bottom to top in the given order
    stacked: { type: Boolean, default: false },
    // Stacked only: print each bar's total above it
    showTotals: { type: Boolean, default: false },
  },
  data() {
    return {
      width: 0,
      hover: null,
      pad: { top: 10, right: 6, bottom: 24, left: 46 },
    };
  },
  computed: {
    plotHeight() {
      return this.height - this.pad.top - this.pad.bottom;
    },
    band() {
      return (this.width - this.pad.left - this.pad.right) / this.labels.length;
    },
    max() {
      if (this.stacked) return Math.max(0, ...this.labels.map((_, i) => this.stackTotal(i)));
      return Math.max(0, ...this.series.flatMap((s) => s.values));
    },
    isEmpty() {
      return this.max === 0;
    },
    // 0 plus 4 "nice" steps covering the max
    ticks() {
      const max = this.max || 1;
      const raw = max / 4;
      const mag = Math.pow(10, Math.floor(Math.log10(raw)));
      const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw);
      const top = Math.max(step, Math.ceil(max / step) * step);
      const ticks = [];
      for (let t = 0; t <= top + step / 2; t += step) ticks.push(Number(t.toPrecision(12)));
      return ticks;
    },
    top() {
      return this.ticks[this.ticks.length - 1];
    },
    barWidth() {
      if (this.stacked) return Math.max(2, Math.min(this.band * 0.5, 56));
      const n = this.series.length;
      const group = Math.min(this.band * 0.62, n * 26);
      return Math.max(2, (group - (n - 1) * 2) / n);
    },
    tooltipStyle() {
      const x = this.bandX(this.hover) + this.band / 2;
      const flip = x > this.width - 150;
      return {
        left: `${flip ? x - 12 : x + 12}px`,
        top: `${this.pad.top}px`,
        transform: flip ? "translateX(-100%)" : "none",
      };
    },
  },
  mounted() {
    this.measure();
    this.observer = new ResizeObserver(this.measure);
    this.observer.observe(this.$refs.root);
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    measure() {
      this.width = this.$refs.root ? this.$refs.root.clientWidth : 0;
    },
    y(value) {
      return this.pad.top + this.plotHeight * (1 - value / this.top);
    },
    bandX(i) {
      return this.pad.left + i * this.band;
    },
    stackTotal(i) {
      return this.series.reduce((sum, s) => sum + (s.values[i] || 0), 0);
    },
    // Stacked segment: square edges, 4px rounded top on the highest segment only
    stackPath(i, j, value) {
      const w = this.barWidth;
      const x = this.bandX(i) + (this.band - w) / 2;
      const below = this.series.slice(0, j).reduce((sum, s) => sum + (s.values[i] || 0), 0);
      const y0 = this.y(below);
      const y1 = this.y(below + value);
      const isTop = this.series.slice(j + 1).every((s) => !s.values[i]);
      const r = isTop ? Math.min(4, w / 2, y0 - y1) : 0;
      return `M${x},${y0} V${y1 + r} Q${x},${y1} ${x + r},${y1} H${x + w - r} Q${x + w},${y1} ${x + w},${y1 + r} V${y0} Z`;
    },
    // Bar anchored on the baseline with 4px rounded data end
    barPath(i, j, value) {
      if (!value) return "";
      if (this.stacked) return this.stackPath(i, j, value);
      const n = this.series.length;
      const group = n * this.barWidth + (n - 1) * 2;
      const x = this.bandX(i) + (this.band - group) / 2 + j * (this.barWidth + 2);
      const w = this.barWidth;
      const y0 = this.y(0);
      const y1 = this.y(value);
      const r = Math.min(4, w / 2, y0 - y1);
      return `M${x},${y0} V${y1 + r} Q${x},${y1} ${x + r},${y1} H${x + w - r} Q${x + w},${y1} ${x + w},${y1 + r} V${y0} Z`;
    },
  },
};
</script>

<style scoped>
.pw-bar {
  position: relative;
  width: 100%;
}

.pw-bar svg {
  display: block;
  overflow: visible;
}

.pw-bar__grid {
  stroke: #e1e0d9;
  stroke-width: 1;
  stroke-dasharray: 2 3;
}

.pw-bar__baseline {
  stroke: #c3c2b7;
  stroke-width: 1;
}

.pw-bar__tick {
  font-size: 11px;
  fill: #898781;
  font-variant-numeric: tabular-nums;
}

.pw-bar__tick--strong {
  fill: #1a2436;
  font-weight: 600;
}

.pw-bar__total {
  font-size: 11px;
  font-weight: 600;
  fill: #1a2436;
  font-variant-numeric: tabular-nums;
}

.pw-bar__wash {
  fill: rgba(16, 24, 40, 0.04);
}

.pw-bar__empty {
  position: absolute;
  inset: 0 0 24px 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  color: #8d99ad;
  pointer-events: none;
}
</style>

<style>
/* Shared tooltip for the dashboard charts */
.pw-tooltip {
  position: absolute;
  z-index: 3;
  pointer-events: none;
  min-width: 140px;
  padding: 8px 10px;
  background: #ffffff;
  border: 1px solid rgba(11, 11, 11, 0.1);
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(16, 24, 40, 0.12);
  font-size: 0.75rem;
  color: #1a2436;
}

.pw-tooltip__title {
  font-weight: 600;
  margin-bottom: 4px;
}

.pw-tooltip__row {
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 1.6;
}

.pw-tooltip__key {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex: 0 0 auto;
}

.pw-tooltip__row--total {
  border-top: 1px solid rgba(11, 11, 11, 0.08);
  margin-top: 2px;
  padding-top: 2px;
}

.pw-tooltip__name {
  color: #52514e;
  flex: 1 1 auto;
}

.pw-tooltip__value {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  margin-left: 12px;
}
</style>
